'use client'

import client from '@/lib/api/client'
import { Content } from '@/lib/categories/categories'
import {
  BroadnessFilterValue,
  defaultBrowseBroadnessFilterValue,
  defaultBrowseSortFilterValue,
  defaultSearchBroadnessFilterValue,
  defaultSearchSortFilterValue,
} from '@/lib/filters/filters'
import { useFiltersStore } from '@/lib/store/filters-store'
import { useAppStore } from '@/lib/store/store'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import styles from '@/components/assets-explorer/filters/filter-sidebar.module.scss'
import { Button } from '@/components/ui/button'
import { FaChevronLeft } from 'react-icons/fa6'
import { PiSlidersHorizontalBold } from 'react-icons/pi'
import SortFilter from './sort-filter'
import { PremiumFilter } from './premium-filter'
import ResolutionFilter from './resolution-filter'
import { Skeleton } from '@/components/ui/skeleton'
import BroadnessFilter from './broadness-filter'
import { Separator } from '@/components/ui/separator'
import { SimpleSearchBar } from '@/components/ui/simple-search-bar'
import { FilterIcon, XIcon } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { useSearchStore } from '@/lib/store/search-store'

type FiltersProps = {
  disabled?: boolean
  className?: string
  defaultBroadness?: number
  refinementEnabled?:
    | boolean
    | {
        search?: boolean
        tags?: boolean
      }
}

// TODO: Translate Tailwind to modular CSS
const FilterSidebar = ({ className, disabled = false, refinementEnabled = true }: FiltersProps) => {
  const filtersVisible = useAppStore((state) => state.filtersVisible)
  const previousFiltersVisible = useAppStore((state) => state.previousFiltersVisible)
  const showFilters = useAppStore((state) => state.showFilters)
  const hideFilters = useAppStore((state) => state.hideFilters)
  const opened = useMemo(() => filtersVisible && !disabled, [filtersVisible, disabled])
  const params = useFiltersStore(useShallow((state) => state.params))
  const filters = useFiltersStore(useShallow((state) => state.filters))
  const setFilterValue = useFiltersStore((state) => state.setFilterValue)
  const clearFilters = useFiltersStore((state) => state.clearFilters)
  const search = useSearchStore((state) => state.search)
  const setSearch = useSearchStore((state) => state.setSearch)
  const availableKeywords = useFiltersStore(useShallow((state) => state.availableKeywords))
  const appliedKeywords = useFiltersStore(useShallow((state) => state.appliedKeywords))
  const setAppliedKeywords = useFiltersStore((state) => state.setAppliedKeywords)
  const toggleKeyword = useFiltersStore((state) => state.toggleKeyword)
  const clearRefinement = useFiltersStore((state) => state.clearRefinement)

  const searchBarRef = useRef<{ clear: () => void }>(null)

  const {
    mainCategory: mainCategorySlug,
    category: categorySlug,
    subCategory: subCategorySlug,
    content: contentSlug,
  } = params

  // Get the Content data to set the default broadness
  const { data: content, isLoading: isLoadingContent } = useQuery({
    enabled: !!mainCategorySlug && !!categorySlug && !!subCategorySlug && !!contentSlug,
    queryKey: ['contents', mainCategorySlug, categorySlug, subCategorySlug, contentSlug],
    queryFn: async ({ signal }) => {
      const { data } = await client.get<Content>(`/contents/${contentSlug}`, {
        params: {
          main_category: mainCategorySlug,
          category: categorySlug,
          sub_category: subCategorySlug,
        },
        signal,
      })
      return data
    },
  })

  const appliedFilterNames = useMemo(() => {
    return filters.filter((filter) => filter.value !== undefined).map((filter) => filter.name)
  }, [filters])

  const hasFiltersApplied = useMemo(() => {
    return appliedFilterNames.length > 0 || appliedKeywords.length > 0 || !!search
  }, [appliedFilterNames.length, appliedKeywords.length, search])

  const toggle = useCallback(() => {
    if (opened) {
      hideFilters()
    } else {
      showFilters()
    }
  }, [hideFilters, opened, showFilters])

  // Sync initial values with storage
  useEffect(() => {
    if (opened) {
      showFilters({
        shouldPersist: false,
      })
    } else {
      hideFilters({
        shouldPersist: false,
      })
    }
  }, [hideFilters, opened, showFilters])

  // Determine if we're in search mode by default, that is, when we only know the main category
  const isSearchMode = useMemo(() => {
    return !categorySlug || !subCategorySlug
  }, [categorySlug, subCategorySlug])

  // Determine teh default Sort value
  const defaultSortValue = useMemo(() => {
    return isSearchMode ? defaultSearchSortFilterValue : defaultBrowseSortFilterValue
  }, [isSearchMode])

  // Determine the defaultBroadness value
  const defaultBroadnessValue: BroadnessFilterValue = useMemo(() => {
    if (content?.searchBroadness) {
      return content.searchBroadness
    }

    return isSearchMode ? defaultSearchBroadnessFilterValue : defaultBrowseBroadnessFilterValue
  }, [content?.searchBroadness, isSearchMode])

  const resetAllFilters = useCallback(() => {
    clearFilters()
    clearRefinement()
    searchBarRef.current?.clear()
  }, [clearFilters, clearRefinement])

  // Count the applied filters (including local refinement)
  const appliedFiltersCount = useMemo(() => {
    return appliedFilterNames.length + appliedKeywords.length + (search ? 1 : 0)
  }, [appliedFilterNames.length, appliedKeywords.length, search])

  const isSearchRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return refinementEnabled.search !== false
  }, [refinementEnabled])

  const isTagsRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return refinementEnabled.tags !== false
  }, [refinementEnabled])

  const isSomeRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return isSearchRefinementEnabled || isTagsRefinementEnabled
  }, [refinementEnabled, isSearchRefinementEnabled, isTagsRefinementEnabled])

  return (
    <motion.div
      data-slot="filter-sidebar"
      className={cn(styles.main, 'tw-h-full', className)}
      initial={{
        minWidth: previousFiltersVisible ? 'var(--filters-opened-width)' : 'var(--filters-closed-width)',
      }}
      animate={{
        minWidth: opened ? 'var(--filters-opened-width)' : 'var(--filters-closed-width)',
      }}
    >
      {/* Header */}
      <div
        className={cn(styles.header, {
          [styles.headerOpened]: opened,
        })}
      >
        {opened && (
          <>
            <div className={styles.titleRow}>
              {hasFiltersApplied && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="!tw-text-[var(--filter-sidebar-color)]"
                  onClick={() => resetAllFilters()}
                >
                  Reset all
                </Button>
              )}
            </div>
            <Button
              type="button"
              title="Hide filters"
              size="icon"
              variant="ghost"
              shape="rounded"
              onClick={() => toggle()}
            >
              <FaChevronLeft className={styles.filtersTrigger} />
            </Button>
          </>
        )}
        {!opened && (
          <Button
            className="tw-relative"
            type="button"
            title="Show filters"
            size="icon"
            variant="ghost"
            shape="rounded"
            disabled={disabled}
            onClick={() => toggle()}
          >
            {!!appliedFiltersCount && (
              <span className="tw-absolute -tw-right-1 -tw-top-1 tw-w-5 tw-rounded-full tw-bg-[#F36C17] tw-py-1 tw-text-xs tw-leading-none tw-text-white">
                {appliedFiltersCount}
              </span>
            )}
            <PiSlidersHorizontalBold className={styles.filtersTrigger} />
          </Button>
        )}
      </div>

      {/* Body */}
      <motion.div
        animate={{
          opacity: opened ? 1 : 0,
          display: opened ? 'block' : 'none',
        }}
      >
        {opened && (
          <div className="tw-flex tw-flex-col tw-py-2">
            <div className="tw-flex tw-flex-col tw-space-y-4">
              {filters.map(({ __typename, label, name, value }) => {
                return (
                  <div key={`${name}-filter`}>
                    {__typename === 'SortFilter' && (
                      <SortFilter
                        triggerClassName="tw-text-[var(--filter-sidebar-color)]"
                        contentClassName="!tw-text-[var(--filter-sidebar-color)]"
                        value={value ?? defaultSortValue}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {__typename === 'PremiumFilter' && (
                      <PremiumFilter
                        value={value}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {__typename === 'ResolutionFilter' && (
                      <ResolutionFilter
                        value={value}
                        onChange={(value) => {
                          setFilterValue(name, value)
                        }}
                      />
                    )}
                    {__typename === 'BroadnessFilter' && (
                      <div>
                        <div className="body_3 tw-mb-2 tw-text-[var(--filter-sidebar-color)]">{label}</div>
                        {isLoadingContent && <Skeleton className="tw-h-6 tw-w-16" />}
                        {!isLoadingContent && (
                          <BroadnessFilter
                            value={value ?? defaultBroadnessValue}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {/* Local refinement */}
            {isSomeRefinementEnabled && (
              <>
                <Separator className="tw-my-4 !tw-bg-[var(--Primary-400)]" />

                <div className="tw-flex tw-flex-col tw-space-y-2">
                  {isSearchRefinementEnabled && (
                    <SimpleSearchBar
                      ref={searchBarRef}
                      placeholder="Filter results"
                      icon={<FilterIcon className="tw-ml-1 tw-h-6 tw-w-6 tw-text-[var(--Primary-200)] tw-opacity-50" />}
                      defaultValue={search ?? ''}
                      onValueChange={(search) => {
                        // Reset search if empty
                        setSearch(search.trim() || undefined)
                      }}
                    />
                  )}

                  {/* Display as a carousel */}

                  {/*<div className="tw-flex tw-max-w-[224px] tw-flex-nowrap tw-items-center">*/}
                  {/*  <div className="-tw-mr-6 tw-w-min tw-max-w-full tw-pr-6">*/}
                  {/*    <Carousel*/}
                  {/*      opts={{*/}
                  {/*        align: 'start',*/}
                  {/*      }}*/}
                  {/*      plugins={[*/}
                  {/*        WheelGesturesPlugin({*/}
                  {/*          forceWheelAxis: 'y',*/}
                  {/*        }),*/}
                  {/*      ]}*/}
                  {/*    >*/}
                  {/*      <CarouselContent className="!tw--ml-2">*/}
                  {/*        {React.Children.toArray(*/}
                  {/*          availableKeywords.map((keyword) => (*/}
                  {/*            <CarouselItem className="tw-max-w-min !tw-pl-2 tw-duration-500 tw-animate-in tw-fade-in">*/}
                  {/*              <Button*/}
                  {/*                type="button"*/}
                  {/*                variant="ghost"*/}
                  {/*                className={cn(*/}
                  {/*                  '!tw-items-center !tw-rounded-full !tw-text-xs !tw-font-light tw-capitalize !tw-text-[var(--Primary-100)] hover:tw-bg-[var(--Primary-300)]',*/}
                  {/*                  {*/}
                  {/*                    '!tw-bg-[var(--Primary-300)] !tw-text-[var(--Primary)]':*/}
                  {/*                      appliedKeywords.includes(keyword),*/}
                  {/*                  }*/}
                  {/*                )}*/}
                  {/*                onClick={() => {*/}
                  {/*                  setAppliedKeywords((prevState) => {*/}
                  {/*                    if (prevState.includes(keyword)) {*/}
                  {/*                      return prevState.filter((kw) => kw !== keyword)*/}
                  {/*                    }*/}
                  {/*                    return [...prevState, keyword]*/}
                  {/*                  })*/}
                  {/*                }}*/}
                  {/*              >*/}
                  {/*                {keyword}*/}
                  {/*                {appliedKeywords.includes(keyword) && (*/}
                  {/*                  <X className="tw-relative tw-left-1 tw-h-4 tw-w-4 tw-opacity-50" />*/}
                  {/*                )}*/}
                  {/*              </Button>*/}
                  {/*            </CarouselItem>*/}
                  {/*          ))*/}
                  {/*        )}*/}
                  {/*      </CarouselContent>*/}
                  {/*    </Carousel>*/}
                  {/*  </div>*/}

                  {/*  /!*"Clear all" button*!/*/}
                  {/*  <div>*/}
                  {/*    {!!availableKeywords.length && (*/}
                  {/*      <Button*/}
                  {/*        title="Clear all filters"*/}
                  {/*        type="button"*/}
                  {/*        variant="transparent"*/}
                  {/*        size="icon"*/}
                  {/*        onClick={() => {*/}
                  {/*          setAppliedKeywords([])*/}
                  {/*        }}*/}
                  {/*        disabled={!appliedKeywords.length}*/}
                  {/*      >*/}
                  {/*        <X className="tw-h-4 tw-w-4" />*/}
                  {/*      </Button>*/}
                  {/*    )}*/}
                  {/*  </div>*/}
                  {/*</div>*/}

                  {/* Display as a flex list */}
                  {isTagsRefinementEnabled && (
                    <div className="tw-flex tw-w-full tw-max-w-[224px] tw-flex-wrap tw-items-center tw-gap-2">
                      {availableKeywords.map((keyword) => (
                        <Button
                          key={keyword}
                          type="button"
                          variant="ghost"
                          className={cn(
                            '!tw-items-center !tw-rounded-full !tw-text-xs !tw-font-light tw-capitalize hover:tw-bg-[var(--Primary-300)] hover:!tw-text-[var(--Primary-100)]',
                            {
                              '!tw-text-[var(--asset-keyword-color)]': !appliedKeywords.includes(keyword),
                              '!tw-bg-[var(--Primary-300)] !tw-text-[var(--Primary-100)]':
                                appliedKeywords.includes(keyword),
                            }
                          )}
                          onClick={() => {
                            toggleKeyword(keyword)
                          }}
                        >
                          {keyword}
                          {appliedKeywords.includes(keyword) && (
                            <XIcon className="tw-relative tw-left-1 tw-h-4 tw-w-4 tw-opacity-50" />
                          )}
                        </Button>
                      ))}
                      {!!availableKeywords.length && (
                        <Button
                          key={'clear-all'}
                          title="Clear all filters"
                          type="button"
                          variant="transparent"
                          size="icon"
                          onClick={() => {
                            setAppliedKeywords([])
                          }}
                          disabled={!appliedKeywords.length}
                        >
                          <XIcon className="tw-h-4 tw-w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/*<Accordion type="multiple" className="w-full" defaultValue={appliedFilterNames}>
              {React.Children.toArray(
                filters.map(({ __typename, label, name, value, ...props }) => {
                  return (
                    <AccordionItem value={name}>
                      <AccordionTrigger className="!tw-p-2">
                        <div className={styles.filterHeader}>
                          <div className="body_3">{label}</div>
                          {!!value && (
                            <div
                              role="button"
                              title={`Clear ${label}`}
                              className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                              onClick={(e) => {
                                e.preventDefault()
                                setFilterValue(name, undefined)
                              }}
                            >
                              Clear
                            </div>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="!tw-px-2 !tw-pb-2">
                        {__typename === 'SortFilter' && (
                          <SortFilter
                            value={value}
                            onChange={(value) => {
                              console.log('SortFilter value', value)
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'PremiumFilter' && (
                          <PremiumFilter
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'ResolutionFilter' && (
                          <ResolutionFilter
                            options={'options' in props ? props.options : []}
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, value)
                            }}
                          />
                        )}
                        {__typename === 'DurationFilter' && (
                          <DurationFilter
                            min={(props as Pick<DurationFilterType, 'min'>).min ?? 0}
                            max={(props as Pick<DurationFilterType, 'max'>).max ?? 0}
                            value={value}
                            onChange={(value) => {
                              setFilterValue(name, undefined)
                            }}
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )
                })
              )}
            </Accordion>*/}
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

export { FilterSidebar }
export type { FiltersProps }
